<template>
    <div class="container ec-page">
        <div class="ec-toolbar">
            <nav class="ec-tabs">
                <button
                    v-for="tab in tabs"
                    :key="tab.key"
                    class="ec-tab"
                    :class="{ 'ec-tab--active': filter === tab.key }"
                    type="button"
                    @click="filter = tab.key"
                >
                    {{ tab.label }}
                    <span class="ec-tab__count">{{ tab.count }}</span>
                </button>
            </nav>
        </div>

        <div class="matrix-panel">
            <div class="table-layout ec-table">
                <div class="table-head">
                    <div class="table-tr">
                        <div class="table-th">{{ __('Participant') }}</div>
                        <div class="table-th">{{ __('EC status') }}</div>
                        <div class="table-th">{{ __('Matched patient') }}</div>
                        <div class="table-th">{{ __('Updated at EC') }}</div>
                        <div class="table-th">{{ __('Note') }}</div>
                        <div class="table-th ec-cell-controls"></div>
                    </div>
                </div>

                <div class="table-body">
                    <div v-if="loading && !trainings.length" class="loader loader--chat loader--grey-background">
                        <i class="fa fa-spinner fa-spin fa-3x fa-fw" aria-hidden="true"></i>
                    </div>

                    <p v-else-if="filteredTrainings.length === 0" class="ec-empty">
                        {{ __('No trainings found.') }}
                    </p>

                    <template v-else>
                        <div
                            v-for="row in filteredTrainings"
                            :key="row.id"
                            class="table-tr ec-row"
                            :class="{ 'ec-row--canceled': row.status === 'CANCELED' }"
                        >
                            <div class="table-td">
                                <div class="ec-participant">
                                    <strong>{{ row.firstname }} {{ row.lastname }}</strong>
                                    <span>{{ row.email || '—' }}</span>
                                    <span>{{ row.phone || '—' }}</span>
                                </div>
                            </div>

                            <div class="table-td">
                                <span v-if="row.status" class="ec-badge" :class="'ec-badge--' + row.status.toLowerCase()">
                                    {{ row.status }}
                                </span>
                                <span
                                    v-else
                                    class="ec-badge ec-badge--missing"
                                    :title="__('This training was not found at ExpertsConnect. It cannot be activated until it appears there.')"
                                >
                                    {{ __('Not at EC') }}
                                </span>
                            </div>

                            <div class="table-td">
                                <div v-if="row.patient" class="ec-patient">
                                    <strong>
                                        {{ row.patient.name }}
                                        <span class="ec-badge ec-badge--match">{{ matchSourceLabel(row.match_source) }}</span>
                                    </strong>
                                    <span>{{ row.patient.email }}</span>
                                    <span v-if="row.patient.registered_at">
                                        {{ __('Registered') }} {{ formatDate(row.patient.registered_at) }}
                                    </span>
                                </div>
                                <span v-else class="ec-muted">{{ __('Not matched yet') }}</span>
                            </div>

                            <div class="table-td">
                                <span v-if="row.remote_updated_at" :title="formatDateTime(row.remote_updated_at)">
                                    {{ fromNow(row.remote_updated_at) }}
                                </span>
                                <span v-else class="ec-muted">—</span>
                            </div>

                            <div class="table-td">
                                <follow-up-note
                                    :case-id="row.id"
                                    :note="row.note"
                                    @save="saveNote"
                                ></follow-up-note>
                            </div>

                            <div class="table-td ec-cell-controls">
                                <div v-if="row.is_hidden" class="ec-hidden">
                                    <VButton
                                        :label="__('unhide')"
                                        :disabled="busy"
                                        variant="secondary"
                                        size="sm"
                                        @onClick="setHidden(row, false)"
                                    />
                                    <span v-if="row.hidden_at" class="ec-hidden__meta">
                                        {{ formatDateTime(row.hidden_at) }}
                                    </span>
                                </div>

                                <div v-else-if="row.is_activated" class="ec-activated">
                                    <span class="ec-activated__label">{{ __('Activated') }}</span>
                                    <span v-if="row.activated_at" class="ec-activated__meta">
                                        {{ formatDateTime(row.activated_at) }}
                                    </span>
                                    <span v-if="row.activated_by" class="ec-activated__meta">
                                        {{ row.activated_by }}
                                    </span>
                                </div>

                                <div v-else class="ec-actions">
                                    <VButton
                                        :label="activatingId === row.id ? __('activating…') : __('activate')"
                                        :disabled="busy"
                                        size="sm"
                                        @onClick="activate(row)"
                                    />
                                    <VButton
                                        :label="__('hide')"
                                        :disabled="busy"
                                        variant="secondary"
                                        size="sm"
                                        @onClick="setHidden(row, true)"
                                    />
                                </div>

                                <p v-if="rowErrors[row.id]" class="ec-row-error">{{ rowErrors[row.id] }}</p>
                            </div>
                        </div>
                    </template>
                </div>
            </div>
        </div>

        <flash-alert
            v-if="alert.show"
            :key="alert.key"
            :text="alert.text"
            :level="alert.level"
            position="top"
            @closeAlert="alert.show = false"
        ></flash-alert>
    </div>
</template>

<script>
import FollowUpNote from '@components/CoachAdmin/FollowUp/FollowUpNote.vue'

export default {
    components: {
        FollowUpNote
    },
    data() {
        return {
            trainings: [],
            loading: true,
            activatingId: null,
            togglingId: null,
            filter: 'all',
            rowErrors: {},
            alert: {
                show: false,
                text: '',
                level: 'success',
                key: 0
            }
        }
    },
    computed: {
        tabs() {
            return [
                { key: 'all', label: this.__('All'), count: this.countBy('all') },
                { key: 'ready', label: this.__('Ready to activate'), count: this.countBy('ready') },
                { key: 'not_matched', label: this.__('Not matched'), count: this.countBy('not_matched') },
                { key: 'activated', label: this.__('Activated'), count: this.countBy('activated') },
                { key: 'hidden', label: this.__('Hidden'), count: this.countBy('hidden') }
            ]
        },
        busy() {
            return this.activatingId !== null || this.togglingId !== null
        },
        filteredTrainings() {
            return this.trainings.filter((row) => this.matchesFilter(row, this.filter))
        }
    },
    async mounted() {
        $(function() {
            $('.item-expertsconnect').addClass('active')
        })

        // Show our own data right away; ExpertsConnect is an outside API that can be slow or down,
        // so its sync runs behind the list instead of in front of it.
        await this.fetch()
        this.sync()
    },
    methods: {
        async fetch() {
            this.loading = true

            try {
                const response = await axios.get('/expertsconnect-trainings/list')

                this.trainings = response.data.data
            } catch (error) {
                this.showAlert(this.__('Loading of trainings failed.'), 'danger')
            }

            this.loading = false
        },
        async sync() {
            try {
                const response = await axios.post('/expertsconnect-trainings/refresh')

                if (response.data.data.updated > 0) {
                    await this.fetch()
                }
            } catch (error) {
                // the list still works, it can just be behind ExpertsConnect; the detail is in the log
                this.showAlert(this.__('Refresh from ExpertsConnect failed.'), 'danger')
            }
        },
        async activate(row) {
            this.activatingId = row.id
            this.$set(this.rowErrors, row.id, null)

            try {
                const response = await axios.post(`/expertsconnect-trainings/${row.id}/activate`)

                this.replaceRow(response.data.data)

                this.showAlert(
                    response.data.result === 'already_activated'
                        ? this.__('Training was already activated at ExpertsConnect.')
                        : this.__('Training activated.')
                )
            } catch (error) {
                this.$set(this.rowErrors, row.id, this.errorMessage(error, this.__('Activation failed.')))
            }

            this.activatingId = null
        },
        async setHidden(row, hidden) {
            this.togglingId = row.id
            this.$set(this.rowErrors, row.id, null)

            try {
                const response = await axios.post(`/expertsconnect-trainings/${row.id}/${hidden ? 'hide' : 'unhide'}`)

                this.replaceRow(response.data.data)
                this.showAlert(hidden ? this.__('Training hidden.') : this.__('Training unhidden.'))
            } catch (error) {
                this.$set(
                    this.rowErrors,
                    row.id,
                    this.errorMessage(error, hidden ? this.__('Hiding failed.') : this.__('Unhiding failed.'))
                )
            }

            this.togglingId = null
        },
        async saveNote({ caseId, note, done }) {
            try {
                const response = await axios.put(`/expertsconnect-trainings/${caseId}/note`, { note })

                this.replaceRow(response.data.data)
                this.showAlert(this.__('Note saved.'))
            } catch (error) {
                this.showAlert(this.errorMessage(error, this.__('Saving the note failed.')), 'danger')
            }

            // the note component stays in its saving state until this is called
            done()
        },
        replaceRow(row) {
            const index = this.trainings.findIndex((item) => item.id === row.id)

            if (index !== -1) {
                this.$set(this.trainings, index, row)
            }
        },
        matchesFilter(row, filter) {
            if (filter === 'hidden') {
                return row.is_hidden
            }

            // A hidden training only shows up under its own tab.
            if (row.is_hidden) {
                return false
            }

            switch (filter) {
                case 'ready':
                    return row.at_ec && row.patient && !row.is_activated
                case 'not_matched':
                    return !row.patient
                case 'activated':
                    return row.is_activated
                default:
                    return true
            }
        },
        countBy(filter) {
            return this.trainings.filter((row) => this.matchesFilter(row, filter)).length
        },
        matchSourceLabel(source) {
            return {
                linked: this.__('linked'),
                email: this.__('e-mail'),
                phone: this.__('phone')
            }[source] || source
        },
        errorMessage(error, fallback) {
            const data = error.response && error.response.data ? error.response.data : {}
            const parts = [data.message, data.result_description].filter(Boolean)

            return parts.length ? parts.join(' — ') : fallback
        },
        showAlert(text, level = 'success') {
            this.alert = {
                show: true,
                text,
                level,
                key: this.alert.key + 1
            }
        },
        fromNow(value) {
            return this.moment(value).fromNow()
        },
        formatDateTime(value) {
            return this.moment(value).format('D. M. YYYY HH:mm')
        },
        formatDate(value) {
            return this.moment(value).format('D. M. YYYY')
        }
    }
}
</script>

<style scoped lang="less">
.ec-page {
    max-width: 1400px;
}

.ec-toolbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 12px;
    margin: 24px 0 16px;
}

.ec-tabs {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
}

.ec-tab {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 6px 14px;
    border: 1px solid #e4e2ee;
    border-radius: 20px;
    background: #fff;
    color: #676486;
    font-family: inherit;
    font-size: 14px;
    cursor: pointer;
    transition: all 0.15s ease;

    &:hover {
        border-color: #5937b7;
        color: #5937b7;
    }

    &--active {
        background: #5937b7;
        border-color: #5937b7;
        color: #fff;

        &:hover {
            color: #fff;
        }

        .ec-tab__count {
            background: rgba(255, 255, 255, 0.25);
            color: #fff;
        }
    }

    &__count {
        padding: 0 7px;
        border-radius: 10px;
        background: #f3f3f6;
        color: #676486;
        font-size: 12px;
        font-weight: 700;
    }
}

.ec-table {
    // Deliberately not using .table-patient: its column widths and flex rows are sized
    // for the patient tables and outrank anything scoped here. This table owns its layout.
    .table-tr {
        display: grid;
        // Ten units: participant 2, status 1, patient 2, updated at EC 1, note 3, buttons 1.
        // minmax(0, ...) because every row is its own grid: an auto minimum would size per row from
        // its content and knock the rows out of line. The buttons get a floor instead, since the
        // widest German one ("wieder anzeigen", 169px) is wider than a unit at these screen sizes.
        grid-template-columns:
            minmax(0, 2fr)
            minmax(0, 1fr)
            minmax(0, 2fr)
            minmax(0, 1fr)
            minmax(0, 3fr)
            minmax(190px, 1fr);
        // every cell starts at the top of the row, as on the leads and follow-up pages
        align-items: start;
        column-gap: 20px;
    }

    .table-head .table-tr {
        padding: 0 20px 10px;
        // German labels are wider than their columns, so they wrap; keep them on one baseline
        align-items: end;
    }

    .table-body .table-tr {
        margin-bottom: 12px;
        padding: 14px 20px;
        border: 1px solid rgba(102, 96, 121, 0.2);
        border-radius: 8px;
        background-color: #fff;
        box-shadow: 0 2px 10px 0 rgba(0, 0, 0, 0.1);
    }

    .table-th {
        padding: 0;
        text-align: left;
        font-size: 11px;
        font-weight: 700;
        letter-spacing: 0.5px;
        text-transform: uppercase;
        color: #b3b1c2;
    }

    .table-td {
        min-width: 0;
        height: auto;
        padding: 0;
        text-align: left;
        background: none;
        font-size: 14px;
        overflow-wrap: break-word;
    }
}

.ec-row {
    transition: background-color 0.15s ease;

    &:hover {
        background-color: #f9f9fa;
    }

    &--canceled {
        opacity: 0.55;
    }
}

.ec-participant,
.ec-patient {
    display: flex;
    flex-direction: column;
    gap: 3px;

    strong {
        color: #2c2857;
    }

    span {
        color: #676486;
        font-size: 13px;
    }
}

.ec-badge {
    display: inline-block;
    padding: 3px 10px;
    border-radius: 12px;
    font-size: 12px;
    font-weight: 700;
    line-height: 1.4;
    background: #f3f3f6;
    color: #676486;

    &--todo {
        background: fade(#2ec9ff, 15%);
        color: darken(#2ec9ff, 20%);
    }

    &--progress {
        background: fade(#fdbd05, 18%);
        color: darken(#fdbd05, 18%);
    }

    &--done {
        background: fade(#00a671, 14%);
        color: #00a671;
    }

    &--canceled {
        background: fade(#ff3e57, 12%);
        color: #ff3e57;
    }

    &--none {
        color: #b3b1c2;
    }

    &--missing {
        background: fade(#ff9f1c, 16%);
        color: darken(#ff9f1c, 18%);
    }

    &--match {
        margin-left: 6px;
        background: fade(#5937b7, 10%);
        color: #5937b7;
        font-weight: 600;
        text-transform: uppercase;
        font-size: 10px;
        letter-spacing: 0.4px;
    }
}

.ec-muted {
    color: #b3b1c2;
}

.ec-empty {
    padding: 40px 0;
    color: #b3b1c2;
    text-align: center;
}

.ec-cell-controls {
    text-align: right;
    white-space: nowrap;
}

.ec-actions {
    display: inline-flex;
    flex-direction: column;
    gap: 6px;

    // VButton sizes itself to its label; stacked, both buttons take the wider one's width.
    .v-btn {
        width: 100%;
    }
}

.ec-hidden {
    display: inline-flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 4px;

    &__meta {
        color: #b3b1c2;
        font-size: 11px;
    }
}

.ec-activated {
    display: inline-flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 2px;

    &__label {
        display: inline-block;
        padding: 3px 12px;
        border-radius: 12px;
        background: fade(#00a671, 14%);
        color: #00a671;
        font-size: 12px;
        font-weight: 700;
    }

    &__meta {
        color: #b3b1c2;
        font-size: 11px;
    }
}

.ec-row-error {
    margin: 6px 0 0;
    color: #ff3e57;
    font-size: 12px;
    white-space: normal;
    max-width: 220px;
}
</style>
